import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  Heading,
  TextInputField,
  FormField,
  Button,
  Pane,
  IconButton,
  SelectField,
  CrossIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Switch,
} from 'evergreen-ui';
import { Container, Row, Col, setConfiguration } from 'react-grid-system';

import { mutate } from 'swr';
import { useForm } from 'react-hook-form';

import { useQueryBanks } from '../../services/api/banksService';
import { getEntry, postEntry, useQueryEntry } from '../../services/api/entriesService';
import { useQueryVitaUserByIdentityId } from '../../services/api/vitaUserService';
import { getIntentColorByJournal, getNameByJournal } from '../../helpers/entriesHelper';
import styles from './EntryFormComponent.module.css';

import { toastNotification } from '../../shared/toastNotification';
import { inputTransferValidation } from '../../helpers/formHelper';
import { useSelector } from 'react-redux';
import { compareAsc } from 'date-fns';
import { convertDate } from '../../helpers/dateConverter';

function TransferCopyFormComponent({ isShown, setIsShown }) {
  const companyId = localStorage.getItem('companyId');
  const userId = localStorage.getItem('userId');
  const [isDisabled, setIsDisabled] = useState(true);
  const [reconciliationDate, setReconciliationDate] = useState('');
  const [fullEntryData, setFullEntryData] = useState(null);

  /* API calls */

  const { data: banks = [], isLoading: isLoadingBanks } = useQueryBanks(
    // company.data.id,
    companyId,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    data: vitaUser,
    isLoading: isLoadingVitaUser,
    isError,
  } = useQueryVitaUserByIdentityId(userId, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const journalEntry = useSelector((state) => state.entryValues.transferCopy);
  /* Form */

  const { formState, register, handleSubmit, reset, getValues, setValue, watch } = useForm({
    defaultValues: {
      ...journalEntry,
      statusPayment: journalEntry?.statusPayment || false,
      value:
        (fullEntryData?.value || journalEntry?.value)?.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
        }) || '0,00',
    },
  });

  setConfiguration({
    containerWidths: '100%',
    gutterWidth: '12',
  });

  useEffect(() => {
    inputTransferValidation(
      watch('bankAccountId'),
      watch('bankAccountIdDestination'),
      watch('dateCompetencia'),
      watch('value'),
      setIsDisabled
    );
  }, [
    watch('bankAccountId'),
    watch('bankAccountIdDestination'),
    watch('dateCompetencia'),
    watch('value'),
  ]);

  const getTransferEntry = (journalEntry) => {
    if (!journalEntry || !banks?.data) return;

    console.log('getTransferEntryJournalEntry: ', journalEntry);
    const tempEntry = { ...journalEntry };
    tempEntry.dateCompetencia = tempEntry.dateCompetencia.substr(0, 10);
    tempEntry.dateVencimento = tempEntry.dateVencimento.substr(0, 10);

    // For transfers, we always want to show:
    // - bankAccountId: source account (Conta de Saída)
    // - bankAccountIdDestination: destination account (Conta de Entrada)
    // Regardless of which entry is being edited

    if (!tempEntry.isInput) {
      // Editing the source entry (saída) - journalEntryRefId is null
      // Source account is this entry's bankAccountId
      // Destination account is from the related entry
      tempEntry.bankAccountId = tempEntry.bankAccountId; // Source (saída)
      tempEntry.bankAccountIdDestination = tempEntry.journalEntryFromRef?.bankAccountId || null; // Destination (entrada)
    } else {
      // Editing the destination entry (entrada) - journalEntryRefId points to source
      // Source account is from the related entry
      // Destination account is this entry's bankAccountId
      const currentBankAccountId = tempEntry.bankAccountId; // Save current account (destination)
      tempEntry.bankAccountId = tempEntry.journalEntryRef?.bankAccountId || null; // Source (saída)
      tempEntry.bankAccountIdDestination = currentBankAccountId; // Destination (entrada) - use saved value
    }

    reset(tempEntry);
    setValue(
      'value',
      tempEntry.value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
      }),
      { shouldDirty: false } // Explicitly mark as not dirty when initializing
    );

    console.log('getTransferEntryUpdated: ', tempEntry);
  };

  // Load full entry data when dialog opens
  useEffect(() => {
    if (isShown && journalEntry?.id && !fullEntryData) {
      getEntry(journalEntry.id)
        .then((response) => {
          if (response.data) {
            setFullEntryData(response.data);
          }
        })
        .catch((error) => {
          console.error('Error loading full entry data:', error);
        });
    } else if (!isShown) {
      setFullEntryData(null);
      reset();
    }
  }, [isShown, journalEntry]);

  useEffect(() => {
    if (isShown && fullEntryData && banks?.data) {
      getTransferEntry(fullEntryData);
    }
  }, [isShown, fullEntryData, banks]);

  const handleInput = (event) => {
    let inputVal = event.target.value.replace(/\D/g, '');
    const initialValue = fullEntryData?.value || journalEntry?.value || 0;

    if (inputVal === '') {
      inputVal = '0';
    }

    // Convert both values to same format for comparison (cents)
    const newValue = Number(inputVal);
    const initialValueInCents = Math.round(initialValue * 100);

    // Format for display
    inputVal = inputVal.padStart(3, '0');
    const wholeNum = inputVal.slice(0, -2);
    const decimal = inputVal.slice(-2);
    const formattedWholeNum = Number(wholeNum).toLocaleString();
    const formattedValue = `${formattedWholeNum},${decimal}`;

    // Compare values in cents to avoid floating point issues
    setValue('value', formattedValue, {
      shouldDirty: newValue !== initialValueInCents,
    });
  };

  const buildFormHeader = () => {
    return (
      <Pane display="flex" flex={1} justifyContent="space-between" alignItems="center">
        <Button
          appearance="primary"
          intent={getIntentColorByJournal(journalEntry)}
          height={48}
          marginRight={16}
        >
          {getNameByJournal(journalEntry)}
        </Button>
        <Button
          appearance="primary"
          height={28}
          marginRight={26}
          style={{
            background: '#FEEAB4',
            color: 'black',
          }}
          cursor="normal"
        >
          Modo Clonar
        </Button>
        <IconButton appearance="minimal" icon={CrossIcon} onClick={onClose} />
      </Pane>
    );
  };

  const buildFormFooter = () => {
    const anyFieldChanged = formState.isDirty;
    console.log('formState: ', formState.isDirty);

    return (
      <Pane>
        <Button
          form="transfer-form"
          type="submit"
          width="fit-content"
          disabled={!anyFieldChanged || isDisabled}
        >
          Salvar
        </Button>
      </Pane>
    );
  };

  const onSubmit = (entry) => {
    console.log('onSubmit');
    let statusResponse = false;
    let toastContent = '';

    // Check if transfer date is before or on reconciliation date for both accounts
    if (banks && banks.data) {
      const sourceBank = banks.data.find((bank) => bank.id === entry.bankAccountId);
      const destBank = banks.data.find((bank) => bank.id === entry.bankAccountIdDestination);

      // Check source bank
      if (sourceBank && sourceBank.reconciliationDate) {
        const compareResult = compareAsc(
          new Date(entry.dateCompetencia),
          new Date(sourceBank.reconciliationDate)
        );

        if (compareResult <= 0) {
          toastNotification(
            'warning',
            `Não é possível criar transferências com data até ${new Date(
              sourceBank.reconciliationDate
            ).toLocaleDateString('pt-BR')}. A conta de origem (${
              sourceBank.name
            }) está conciliada até esta data.`
          );
          return;
        }
      }

      // Check destination bank
      if (destBank && destBank.reconciliationDate) {
        const compareResult = compareAsc(
          new Date(entry.dateCompetencia),
          new Date(destBank.reconciliationDate)
        );

        if (compareResult <= 0) {
          toastNotification(
            'warning',
            `Não é possível criar transferências com data até ${new Date(
              destBank.reconciliationDate
            ).toLocaleDateString('pt-BR')}. A conta de destino (${
              destBank.name
            }) está conciliada até esta data.`
          );
          return;
        }
      }
    }

    let entryToCreate = {
      companyId: companyId,
      journalEntryType: 5,
      isInput: false, // Transfer is an outflow from source account
      dateCompetencia: entry.dateCompetencia,
      dateVencimento: entry.dateCompetencia,
      userUpdateId: !isLoadingVitaUser && vitaUser?.data?.id,
      userCreateId: !isLoadingVitaUser && vitaUser?.data?.id,
      description: entry.description,
      statusPayment: entry.statusPayment,
      paymentType: 1,
      value: Number(entry.value.replace(/\./g, '').replace(',', '.')),
      bankAccountId: entry.bankAccountId,
      bankAccountIdDestination: entry.bankAccountIdDestination,
    };

    postEntry(entryToCreate)
      .then((res) => {
        console.log('res post entry: ', res);
        mutate('entriesFilteredByDate');
        mutate((key) => typeof key === 'string' && key.startsWith('bankBalances-'));
        mutate((key) => typeof key === 'string' && key.startsWith('dashboardBalances-'));
        statusResponse = 'success';
        toastContent = 'Lançamento salvo com sucesso.';
        onClose();
      })
      .catch((err) => {
        statusResponse = 'warning';
        console.log('post err', err);
        toastContent = `Verifique os campos novamente.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
        mutate('banks');
      });
  };

  const onClose = () => {
    reset();
    setIsShown(false);
  };

  useEffect(() => {
    const handleReconciliationDate = () => {
      if (!banks) return;
      if (watch('bankAccountId') && watch('bankAccountIdDestination')) {
        const bankAccount = banks.data.find((bank) => bank.id === watch('bankAccountId'));
        const bankAccountDestination = banks.data.find(
          (bank) => bank.id === watch('bankAccountIdDestination')
        );

        if (bankAccount.reconciliationDate && bankAccountDestination.reconciliationDate) {
          console.log(
            'Resultado da comparação: ',
            compareAsc(
              new Date(bankAccount.reconciliationDate),
              new Date(bankAccountDestination.reconciliationDate)
            )
          );
          console.log('Data 1', new Date(bankAccount.reconciliationDate));
          console.log('Data 2', new Date(bankAccountDestination.reconciliationDate));
          if (
            compareAsc(
              new Date(bankAccount.reconciliationDate),
              new Date(bankAccountDestination.reconciliationDate)
            ) === -1
          ) {
            console.log('reconciliationDate 1 é menor');
            setReconciliationDate(new Date(bankAccountDestination.reconciliationDate));
          } else {
            console.log('reconciliationDate 2 é menor');
            setReconciliationDate(new Date(bankAccount.reconciliationDate));
          }
          return;
        }
      }

      if (!banks || !banks.data) {
        setReconciliationDate('');
        return;
      }

      const bankAccountId = watch('bankAccountId');
      const bankAccountIdDestination = watch('bankAccountIdDestination');

      if (!bankAccountId && !bankAccountIdDestination) {
        setReconciliationDate('');
        return;
      }

      const bank = banks.data.find(
        (bank) => bank.id === bankAccountId || bank.id === bankAccountIdDestination
      );

      if (!bank || !bank.reconciliationDate) {
        setReconciliationDate('');
        return;
      }
      setReconciliationDate(new Date(bank.reconciliationDate));
    };

    handleReconciliationDate();
  }, [watch('bankAccountId'), watch('bankAccountIdDestination'), banks]);

  return (
    <Dialog
      isShown={isShown}
      title=""
      onCloseComplete={onClose}
      confirmLabel="Custom Label"
      hasFooter={true}
      header={() => buildFormHeader()}
      footer={buildFormFooter}
      // shouldCloseOnOverlayClick={formState.isDirty ? onOverlayClick() : true}
      shouldCloseOnEscapePress={!formState.isDirty}
      width={800}
      backgroundColor="#f8f8f8"
    >
      <div className={styles.dialogWrapper}>
        <form id="transfer-form" onSubmit={handleSubmit(onSubmit)}>
          <FormField className={styles.formArea}>
            <Container
              fluid={true}
              style={{
                width: '100%',
                margin: 'none',
                padding: 'none',
              }}
            >
              <Row>
                <Col sm={12} md={12} lg={6}>
                  <SelectField
                    {...register('bankAccountId')}
                    label="Conta de Saída"
                    defaultValue={undefined}
                    onChange={(e) => setValue('bankAccountId', e.target.value)}
                  >
                    <option value={undefined}></option>
                    {!isLoadingBanks &&
                      banks.data.map((bank) => {
                        return (
                          <option key={bank.id} value={bank.id}>
                            {bank.name}
                          </option>
                        );
                      })}
                  </SelectField>
                </Col>
                <Col sm={12} md={12} lg={6}>
                  <SelectField
                    {...register('bankAccountIdDestination')}
                    label="Conta de Entrada"
                    defaultValue={undefined}
                    onChange={(e) => setValue('bankAccountIdDestination', e.target.value)}
                  >
                    <option value={undefined}></option>

                    {!isLoadingBanks &&
                      banks.data.map((bank) => {
                        return (
                          <option key={bank.id} value={bank.id}>
                            {bank.name}
                          </option>
                        );
                      })}
                  </SelectField>
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={6} lg={6}>
                  <TextInputField
                    {...register('dateCompetencia')}
                    label="Data da Transferência"
                    id="dateCompetencia"
                    name="dateCompetencia"
                    type="date"
                    onChange={(e) => {
                      setValue('dateCompetencia', e.target.value);
                    }}
                    min={convertDate(reconciliationDate)}
                  />
                </Col>
                <Col sm={12} md={6} lg={6}>
                  <TextInputField
                    {...register('description')}
                    label="Descrição"
                    id="description"
                    name="description"
                    placeholder=""
                    defaultValue=""
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={10} sm={11} md={11} lg={12}>
                  <TextInputField
                    {...register('value', {
                      required: true,
                      // min: 0,
                      // valueAsNumber: true,
                    })}
                    defaultValue="0,00"
                    label="Valor (R$)"
                    type="text"
                    // min='0,00'
                    id="value"
                    name="value"
                    placeholder=""
                    // step='0.01'
                    onChange={handleInput}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={2} sm={1} md={1} lg={1}>
                  <Heading size={400} marginBottom={16}>
                    Pago
                  </Heading>
                  <Switch
                    {...register('statusPayment')}
                    checked={watch('statusPayment')}
                    onChange={(e) => {
                      setValue('statusPayment', e.target.checked);
                    }}
                  />
                </Col>
              </Row>
            </Container>
          </FormField>
        </form>
      </div>
    </Dialog>
  );
}

export default TransferCopyFormComponent;
